import { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit, doc, deleteDoc, updateDoc, Timestamp, where } from 'firebase/firestore';
import { db } from '../../firebase';
import {
    Search,
    RefreshCw,
    Trash2,
    Edit,
    Filter,
    Globe,
    Monitor,
    Hash,
    X,
    Save,
    ExternalLink
} from 'lucide-react';

interface Click {
    id: string;
    affiliate_id: string;
    product_id: string;
    sub_id?: string;
    source_id?: string;
    ip_address: string;
    country: string;
    city?: string;
    os_name?: string;
    browser_name?: string;
    device_type?: string;
    referer?: string;
    landing_page?: string;
    status: 'active' | 'converted' | 'rejected';
    rejected_reason?: string;
    timestamp: any;
}

export default function AdminClicks() {
    const [clicks, setClicks] = useState<Click[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [editingClick, setEditingClick] = useState<Click | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    // Edit form state
    const [editData, setEditData] = useState({
        sub_id: '',
        source_id: '',
        status: '' as any,
        rejected_reason: ''
    });

    useEffect(() => {
        fetchClicks();
    }, [statusFilter]);

    const fetchClicks = async () => {
        setLoading(true);
        try {
            let clicksQuery = query(collection(db, 'clicks'), orderBy('timestamp', 'desc'), limit(100));

            if (statusFilter !== 'all') {
                clicksQuery = query(
                    collection(db, 'clicks'),
                    where('status', '==', statusFilter),
                    orderBy('timestamp', 'desc'),
                    limit(100)
                );
            }

            const snapshot = await getDocs(clicksQuery);
            setClicks(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Click[]);
        } catch (error) {
            console.error('Error fetching clicks:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this click? This will remove all tracking history for this click.')) return;

        setActionLoading(true);
        try {
            await deleteDoc(doc(db, 'clicks', id));
            setClicks(clicks.filter(c => c.id !== id));
        } catch (error) {
            console.error('Error deleting click:', error);
            alert('Failed to delete click');
        } finally {
            setActionLoading(false);
        }
    };

    const openEditModal = (click: Click) => {
        setEditingClick(click);
        setEditData({
            sub_id: click.sub_id || '',
            source_id: click.source_id || '',
            status: click.status,
            rejected_reason: click.rejected_reason || ''
        });
        setShowEditModal(true);
    };

    const handleUpdate = async () => {
        if (!editingClick) return;

        setActionLoading(true);
        try {
            await updateDoc(doc(db, 'clicks', editingClick.id), {
                ...editData,
                updated_at: Timestamp.now()
            });
            setShowEditModal(false);
            fetchClicks();
        } catch (error) {
            console.error('Error updating click:', error);
            alert('Failed to update click');
        } finally {
            setActionLoading(false);
        }
    };

    const formatDate = (ts: any) => {
        if (!ts) return '-';
        const date = ts.toDate ? ts.toDate() : new Date(ts);
        return date.toLocaleString();
    };

    const filteredClicks = clicks.filter(c => {
        if (!searchTerm) return true;
        const s = searchTerm.toLowerCase();
        return (
            c.id.toLowerCase().includes(s) ||
            c.affiliate_id.toLowerCase().includes(s) ||
            c.product_id.toLowerCase().includes(s) ||
            c.ip_address.toLowerCase().includes(s) ||
            (c.sub_id && c.sub_id.toLowerCase().includes(s)) ||
            (c.source_id && c.source_id.toLowerCase().includes(s)) ||
            (c.landing_page && c.landing_page.toLowerCase().includes(s))
        );
    });

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active': return 'text-emerald-400 bg-emerald-500/10';
            case 'converted': return 'text-blue-400 bg-blue-500/10';
            case 'rejected': return 'text-red-400 bg-red-500/10';
            default: return 'text-gray-400 bg-gray-500/10';
        }
    };

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-white">Click Traffic</h1>
                    <p className="text-gray-400">Monitor and manage raw click data</p>
                </div>
                <button onClick={fetchClicks} disabled={loading} className="btn-secondary flex items-center gap-2">
                    <RefreshCw size={20} className={loading ? 'animate-spin' : ''} /> Refresh
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2 relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input
                        type="text"
                        placeholder="Search by ID, IP, Affiliate ID, Product ID, Sub ID..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="input-field pl-12"
                    />
                </div>
                <div className="relative">
                    <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="input-field pl-12"
                    >
                        <option value="all">All Statuses</option>
                        <option value="active">Active</option>
                        <option value="converted">Converted</option>
                        <option value="rejected">Rejected</option>
                    </select>
                </div>
            </div>

            <div className="card overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                    <thead>
                        <tr className="border-b border-white/5 text-gray-400 uppercase tracking-wider">
                            <th className="py-4 px-4 font-medium">Date</th>
                            <th className="py-4 px-4 font-medium">Click ID</th>
                            <th className="py-4 px-4 font-medium">Affiliate</th>
                            <th className="py-4 px-4 font-medium">IP / Country</th>
                            <th className="py-4 px-4 font-medium">Browser / OS</th>
                            <th className="py-4 px-4 font-medium">Landing Page</th>
                            <th className="py-4 px-4 font-medium">Status</th>
                            <th className="py-4 px-4 font-medium text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                        {loading ? (
                            <tr>
                                <td colSpan={8} className="py-12 text-center">
                                    <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
                                </td>
                            </tr>
                        ) : filteredClicks.length === 0 ? (
                            <tr>
                                <td colSpan={8} className="py-12 text-center text-gray-500">
                                    No clicks found
                                </td>
                            </tr>
                        ) : (
                            filteredClicks.map((click) => (
                                <tr key={click.id} className="hover:bg-white/5 transition-colors">
                                    <td className="py-4 px-4 text-gray-400">
                                        {formatDate(click.timestamp)}
                                    </td>
                                    <td className="py-4 px-4 font-mono text-gray-300">
                                        <div className="flex flex-col">
                                            <span className="select-all" title={click.id}>{click.id.substring(0, 8)}...</span>
                                            <span className="text-[10px] text-gray-500 truncate max-w-[100px]">{click.product_id}</span>
                                        </div>
                                    </td>
                                    <td className="py-4 px-4 text-gray-300">
                                        <div className="flex flex-col">
                                            <span>{click.affiliate_id.substring(0, 8)}...</span>
                                            {click.sub_id && <span className="text-[10px] text-primary-400">s1: {click.sub_id}</span>}
                                            {click.source_id && <span className="text-[10px] text-accent-400">src: {click.source_id}</span>}
                                        </div>
                                    </td>
                                    <td className="py-4 px-4 text-gray-300">
                                        <div className="flex flex-col">
                                            <span className="flex items-center gap-1"><Hash size={10} className="text-gray-500" />{click.ip_address}</span>
                                            <span className="flex items-center gap-1 text-[10px] text-gray-500"><Globe size={10} />{click.country} {click.city ? `, ${click.city}` : ''}</span>
                                        </div>
                                    </td>
                                    <td className="py-4 px-4 text-gray-300">
                                        <div className="flex flex-col">
                                            <span className="flex items-center gap-1"><Monitor size={10} className="text-gray-500" />{click.browser_name}</span>
                                            <span className="text-[10px] text-gray-500">{click.os_name} {click.device_type !== 'desktop' ? `(${click.device_type})` : ''}</span>
                                        </div>
                                    </td>
                                    <td className="py-4 px-4 text-gray-300 max-w-[200px]">
                                        {click.landing_page ? (
                                            <a
                                                href={click.landing_page}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-1 text-primary-400 hover:text-primary-300 transition-colors truncate"
                                                title={click.landing_page}
                                            >
                                                <ExternalLink size={10} className="flex-shrink-0" />
                                                <span className="truncate">{(() => { try { const u = new URL(click.landing_page!); return u.hostname + u.pathname; } catch { return click.landing_page; } })()}</span>
                                            </a>
                                        ) : (
                                            <span className="text-gray-600">-</span>
                                        )}
                                    </td>
                                    <td className="py-4 px-4">
                                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium uppercase ${getStatusColor(click.status)}`}>
                                            {click.status}
                                        </span>
                                    </td>
                                    <td className="py-4 px-4 text-right">
                                        <div className="flex justify-end gap-1">
                                            <button
                                                onClick={() => openEditModal(click)}
                                                className="p-1.5 hover:text-primary-400 transition-colors bg-white/5 rounded"
                                                title="Edit"
                                            >
                                                <Edit size={14} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(click.id)}
                                                className="p-1.5 hover:text-red-400 transition-colors bg-white/5 rounded"
                                                title="Delete"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Edit Modal */}
            {showEditModal && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className="card w-full max-w-md animate-scale-in">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-white">Edit Click Status</h2>
                            <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-white">
                                <X size={24} />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Sub ID (s1)</label>
                                <input
                                    type="text"
                                    value={editData.sub_id}
                                    onChange={(e) => setEditData({ ...editData, sub_id: e.target.value })}
                                    className="input-field"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Source ID</label>
                                <input
                                    type="text"
                                    value={editData.source_id}
                                    onChange={(e) => setEditData({ ...editData, source_id: e.target.value })}
                                    className="input-field"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                                <select
                                    value={editData.status}
                                    onChange={(e) => setEditData({ ...editData, status: e.target.value as any })}
                                    className="input-field"
                                >
                                    <option value="active">Active (Pending)</option>
                                    <option value="converted">Converted</option>
                                    <option value="rejected">Rejected</option>
                                </select>
                            </div>

                            {editData.status === 'rejected' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2 text-red-400">Rejection Reason</label>
                                    <textarea
                                        value={editData.rejected_reason}
                                        onChange={(e) => setEditData({ ...editData, rejected_reason: e.target.value })}
                                        className="input-field h-20"
                                        placeholder="Reason for rejection..."
                                    />
                                </div>
                            )}

                            <div className="flex gap-3 pt-4">
                                <button onClick={() => setShowEditModal(false)} className="btn-secondary flex-1">Cancel</button>
                                <button
                                    onClick={handleUpdate}
                                    disabled={actionLoading}
                                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                                >
                                    {actionLoading ? <RefreshCw className="animate-spin" size={18} /> : <Save size={18} />}
                                    Update Click
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
